/**
 * @module components/LinkedEntityList
 * @description Shared list rendering linked Claims oder Cases (#1246 Smart-Buttons).
 *   Empty-State, Row mit Header (Number/Title) + Meta-Zeile (Kunde, Datum, Link-Type-Badge),
 *   Delete-Button (außer link_type='converted_from'). Click-Row → Detail-Navigation.
 *
 * @since v2.539.0
 */
import { useLanguage } from '../context/LanguageContext';
import { Lock, X, Plus, ExternalLink } from 'lucide-react';

const LINK_TYPE_LABEL = {
  manual: 'links.type.manual',
  converted_from: 'links.type.convertedFrom',
  parent: 'links.type.parent',
  related: 'links.type.related',
};

const LINK_TYPE_COLOR = {
  manual: 'bg-surface-inset text-content-secondary',
  converted_from: 'bg-amber-50 text-amber-700 border border-amber-200',
  parent: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  related: 'bg-surface-inset text-content-secondary',
};

export default function LinkedEntityList({
  rows,
  loading,
  entityType,
  onOpen,
  onUnlink,
  onLinkClick,
  unlinking,
}) {
  const { t } = useLanguage();

  if (loading) {
    return <div className="text-[13px] text-content-secondary py-4">{t('common.loading', 'Lädt...')}</div>;
  }

  const renderItem = (row) => {
    const isConvertedFrom = row.link_type === 'converted_from';
    const title = entityType === 'case'
      ? (row.title || `Case #${row.case_id}`)
      : (row.claim_number || `#${row.claim_id}`);
    const idDisplay = entityType === 'case' ? `#${row.case_id}` : `#${row.claim_id}`;
    // #1313 (BR-50) — Pfad /cases/<id> statt Query /cases?id=<id>: die Route /cases
    // leitet auf /cases/liste um und verwirft die Query; Detail-Route ist der Pfad.
    const targetUrl = entityType === 'case'
      ? `/cases/${row.case_id}`
      : `/claims/${row.claim_id}`;

    const dateLabel = row.linked_at
      ? new Date(row.linked_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';

    // #1248 — Activity-Indicators: Indigo-Dot (neue Aktivität seit letztem Besuch),
    // Amber-Dot (ungelesene Nachrichten — nur Claims, da Cases keinen Read-State haben)
    const hasNewActivity = !!row.has_new_activity;
    const unreadCount = Number(row.unread_messages_count) || 0;
    const lastPreview = (row.last_message_preview || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const lastAt = row.last_message_at
      ? new Date(row.last_message_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';

    return (
      <div
        key={row.link_id}
        className="flex items-start justify-between gap-3 p-3 border border-line-card rounded-card bg-surface-card hover:bg-surface-hover transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <button
              type="button"
              onClick={() => onOpen?.(row, targetUrl)}
              className="text-[13px] font-semibold text-content hover:text-[var(--text-link,#338edc)] truncate flex items-center gap-1"
            >
              <span className="text-content-tertiary">{idDisplay}</span>
              <span className="truncate">{title}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </button>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${LINK_TYPE_COLOR[row.link_type] || LINK_TYPE_COLOR.manual}`}>
              {t(LINK_TYPE_LABEL[row.link_type] || LINK_TYPE_LABEL.manual)}
            </span>
            {isConvertedFrom && <Lock className="w-3 h-3 text-amber-600" title={t('links.immutable', 'Permanent')} />}
            {/* #1248 — Indigo-Dot: neue Aktivität seit letztem Besuch */}
            {hasNewActivity && (
              <span
                className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-indigo-500 shrink-0"
                title={t('links.newActivity', 'Neu seit letztem Besuch')}
              />
            )}
            {/* #1248 — Amber-Dot: ungelesene Nachrichten (Claims only) */}
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-amber-500 shrink-0"
                title={(t('links.unread', '{n} ungelesen') || '{n} ungelesen').replace('{n}', String(unreadCount))}
              />
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-content-tertiary flex-wrap">
            {row.customer_name && <span>{row.customer_name}</span>}
            {row.customer_name && dateLabel && <span>•</span>}
            {dateLabel && <span>{t('links.linkedOn', 'Verknüpft am')} {dateLabel}</span>}
            {row.linked_by_name && (
              <>
                <span>•</span>
                <span>{row.linked_by_name}</span>
              </>
            )}
            {row.note && (
              <>
                <span>•</span>
                <span className="italic">{row.note}</span>
              </>
            )}
          </div>
          {/* #1248 — Preview der letzten Nachricht */}
          {lastPreview && (
            <div className="mt-1.5 text-[11px] text-content-secondary line-clamp-1" title={lastPreview}>
              <span className="text-content-tertiary mr-1">
                {t('links.lastMessage', 'Letzte Nachricht')}{lastAt ? ` · ${lastAt}` : ''}:
              </span>
              <span>{lastPreview}</span>
            </div>
          )}
        </div>
        {!isConvertedFrom && (
          <button
            type="button"
            disabled={unlinking === row.link_id}
            onClick={() => onUnlink?.(row.link_id)}
            className="shrink-0 p-1.5 text-content-tertiary hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
            title={t('links.unlink', 'Verknüpfung entfernen')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-content-tertiary">
          {rows.length === 0
            ? t('links.empty', 'Keine Verknüpfungen')
            : t('links.count', '{n} Verknüpfung(en)').replace('{n}', String(rows.length))}
        </div>
        {onLinkClick && (
          <button
            type="button"
            onClick={onLinkClick}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium bg-[var(--text-link,#338edc)] text-white rounded hover:opacity-90 transition-opacity"
          >
            <Plus className="w-3.5 h-3.5" />
            {entityType === 'case' ? t('links.addCase', 'Case verknüpfen') : t('links.addClaim', 'Klärung verknüpfen')}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8 text-[13px] text-content-tertiary border border-dashed border-line rounded-card">
          {entityType === 'case'
            ? t('links.noCases', 'Keine verknüpften Cases.')
            : t('links.noClaims', 'Keine verknüpften Klärungen.')}
        </div>
      ) : (
        <div className="space-y-2">{rows.map(renderItem)}</div>
      )}
    </div>
  );
}
